import { addDays } from 'date-fns/addDays';
import { subDays } from 'date-fns/subDays';
import { isAfter } from 'date-fns/isAfter';
import { cached, tracked } from '@glimmer/tracking';
// eslint-disable-next-line import-x/no-named-as-default
import DS from 'ember-data';
import { guidFor } from '@ember/object/internals';
import { formatDateQuery } from 'uplisting-frontend/utils';
import BaseRepositoryService from 'uplisting-frontend/services/repositories/base';
import CalendarModel from 'uplisting-frontend/models/calendar';

interface IFetchCalendarParams {
  start_date: string;
  end_date: string;
  include: string;
  property_id?: string;
}

interface IOptions {
  startDate: Date;
  endDate?: Date;
  ids?: string;
  forceReload?: boolean;
}

const include = [
  'rates',
  'bookings',
  'bookings.property',
  'bookings.multi_unit',
  'changeovers',
  'changeovers.booking',
  'changeovers.property',
  'changeovers.multi_unit',
].join(',');

export default class CalendarRepositoryService extends BaseRepositoryService<CalendarModel> {
  recordName = 'calendar';
  implementMethods = ['query'];

  #cacheData = new Map();

  public itemsPerPage = 21;

  @cached @tracked dateOptions: Date[] = [];
  @cached @tracked minItem = 0;

  @cached
  get maxItem(): number {
    if (this.dateOptions?.length) {
      return this.dateOptions.length;
    }

    return this.itemsPerPage;
  }

  @cached
  get nextStartDate(): Date {
    const startDate = this.dateOptions[this.maxItem - 1] as Date;

    return addDays(startDate, 1);
  }

  @cached
  get prevStartDate(): Date {
    const endDate = this.dateOptions[0] as Date;

    return subDays(endDate, this.itemsPerPage);
  }

  public async loadData(options: IOptions): Promise<CalendarModel> {
    const { startDate, ids, forceReload } = options;
    const endDate = options.endDate
      ? options.endDate
      : addDays(startDate, this.itemsPerPage);

    const cacheKey = this.#buildCacheKey(startDate, endDate);

    if (this.#cacheData.has(cacheKey) && !forceReload) {
      return this.#cacheData.get(cacheKey);
    }

    const params: IFetchCalendarParams = {
      start_date: formatDateQuery(startDate),
      end_date: formatDateQuery(endDate),
      include,
    };

    if (ids) {
      params.property_id = ids;
    }

    this.#setDateOptions(startDate);

    const data = (await this.query(params))[0] as CalendarModel;

    this.#cacheData.set(cacheKey, data);

    return data;
  }

  public async loadNext(ids: string): Promise<DS.PromiseObject<CalendarModel>> {
    return this.loadData({ startDate: this.nextStartDate, ids });
  }

  public async loadPrev(ids: string): Promise<DS.PromiseObject<CalendarModel>> {
    return this.loadData({ startDate: this.prevStartDate, ids });
  }

  #setDateOptions(startDate: Date): void {
    const result = this.#computeDateOptions(startDate);

    if (!this.dateOptions.length) {
      // initial setup
      this.dateOptions = [...result];
    } else if (
      isAfter(result[0] as Date, this.dateOptions[this.maxItem - 1] as Date)
    ) {
      // setting next dates
      this.dateOptions = [...this.dateOptions, ...result];
    } else {
      // setting prev dates
      this.dateOptions = [...result, ...this.dateOptions];
    }
  }

  #computeDateOptions(startDate: Date): Date[] {
    const result: Date[] = [];

    for (let i = 0; i < this.itemsPerPage + 1; i++) {
      result.push(startDate);

      startDate = addDays(startDate, 1);
    }

    return result;
  }

  #buildCacheKey(startDate: Date, endDate: Date): string {
    return guidFor(formatDateQuery(startDate) + formatDateQuery(endDate));
  }
}

declare module '@ember/service' {
  interface Registry {
    'repositories/calendar': CalendarRepositoryService;
  }
}
